import React, { Component, Suspense, lazy } from "react";
import { BrowserRouter, Route } from "react-router-dom";
import { HMR } from "@pwa/preset-react";
import { Provider as ReduxProvider } from "react-redux";
import store from "@utils/redux/store";
import { FetchInAppServerContextError } from "@utils/context";
import { ParentRouterContext } from "@utils/ParentRouterContext";
import {
  initializeGlobalServicesWithBotDetection,
  getServerContextOnInitialization,
  updateStoreWithServerContext,
} from "@utils/initialization";

import ExperimentTracker from "@utils/services/ExperimentTracker";
import { I18nextProvider } from "react-i18next";
import i18n from "@i18n";

import ROUTES from "./routes";

import "./styles/global.less";

/**
 * Main application routing.
 *
 * This BrowserRouter is used to duplicate the paths that Django used to provide
 * and allows us to use a single SPA instead of multiple bundles. HashRouter is
 * used in the individual pages to maintain the existing routing structure.
 *
 * We pass the BrowserRouter's match object into our Page App components which use
 * HashRouter as the parentRouteMatch prop so we have access to the BrowserRouter's
 * match metadata.
 */
class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      appInitialized: false,
    };

    this.initializeAndTrackMeristemV2Experiment = this.initializeAndTrackMeristemV2Experiment.bind(
      this
    );
  }

  async componentDidMount() {
    let serverContext;
    try {
      // First get the serverContext from cache or refetch.
      serverContext = await getServerContextOnInitialization();
      // Second rehydrate redux state with serverContext and fetch userData if an existing user.
      await updateStoreWithServerContext(serverContext);
      // Third initialize the various services we use to sync data.
      initializeGlobalServicesWithBotDetection(serverContext);
    } catch (e) {
      if (e instanceof FetchInAppServerContextError) {
        window.location = `/identification-error.html${window.location.search}`;
      } else {
        window.location = `/error.html${window.location.search}`;
      }

      // Throw another error to stop the rest of the JS from running after the redirect
      console.error(e);
      throw new Error("Failed to initialize App");
    }

    // Initialize tracking utilizing the embedded window.meristemContext.
    this.initializeAndTrackMeristemV2Experiment();

    // Finally allow application to render.
    this.setState({ appInitialized: true });

    // After user being on the buyflow for at least 30 seconds, trigger mixpanel tracking for GoodClick
    setTimeout(() => {
      window.tracker.trackGenericEvent("GoodClick");
    }, 30000);
  }

  /**
   * Initialize the Visit Tracker with Experiment tags and track OnMeristemExperimentDecided
   */
  initializeAndTrackMeristemV2Experiment() {
    const experimentsState = window?.meristemContext?.experimentsCookie || [];
    window.visitTracker.init(
      ExperimentTracker.formatMeristemExperimentStateVisitData(
        experimentsState
      ) || {}
    );

    experimentsState.forEach(experimentState => {
      window.tracker.trackGenericEvent("OnMeristemExperimentDecided", {
        experimentName: experimentState.experimentName || null,
        variationName: experimentState.variationName || null,
      });
    });
  }

  render() {
    return (
      <BrowserRouter ref={this.routerRef}>
        <Suspense fallback={<div />}>
          {this.state.appInitialized &&
            ROUTES.map(route => (
              <Route
                key={`${route.path}`}
                path={route.path}
                exact={route.exact || false}
                render={({ history, match }) => (
                  <I18nextProvider i18n={i18n}>
                    <ReduxProvider store={store}>
                      <ParentRouterContext.Provider
                        value={{
                          history,
                          match,
                        }}
                      >
                        <route.component branch={this.state.branch} />
                      </ParentRouterContext.Provider>
                    </ReduxProvider>
                  </I18nextProvider>
                )}
              />
            ))}
        </Suspense>
      </BrowserRouter>
    );
  }
}

export default HMR(App, module);
